import React, { useCallback, useEffect, useState } from 'react';
import { notes as apiNotes, validation } from '../services/api';
import './Validation.css';
import { eur, km, dateFr } from '../services/format';
import Visionneuse from './Visionneuse';

// L'écran du valideur. Mobile : la liste puis le détail plein écran.
// Bureau (≥ 1024 px) : maître-détail — la liste reste à gauche, le détail
// s'ouvre à droite, on enchaîne les notes sans « retour ».
function Validation() {
  const [liste, setListe] = useState([]);
  const [selection, setSelection] = useState(null); // { note, depenses }
  const [ecartees, setEcartees] = useState({}); // depense_id -> motif
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [apercu, setApercu] = useState(null);

  const charger = useCallback(() => {
    validation.enAttente().then((res) => setListe(res.data.notes)).catch(() => {});
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const ouvrir = async (id) => {
    setErreur('');
    setEcartees({});
    try {
      const res = await apiNotes.lire(id);
      setSelection(res.data);
    } catch (err) {
      setErreur(err.response?.data?.error || 'Note illisible');
    }
  };

  const basculerEcart = (depId) => {
    setEcartees((e) => {
      const copie = { ...e };
      if (depId in copie) delete copie[depId];
      else copie[depId] = '';
      return copie;
    });
  };

  const valider = async () => {
    setErreur('');
    const lignes = Object.entries(ecartees).map(([depense_id, motif]) => ({ depense_id: parseInt(depense_id, 10), motif }));
    if (lignes.some((l) => !l.motif.trim())) {
      setErreur('Chaque ligne écartée doit porter un motif — le collaborateur doit savoir quoi corriger.');
      return;
    }
    setEnvoi(true);
    try {
      await validation.valider(selection.note.id, lignes);
      setSelection(null);
      charger();
    } catch (err) {
      setErreur(err.response?.data?.error || 'Validation impossible');
    } finally {
      setEnvoi(false);
    }
  };

  const refuser = async () => {
    const motif = window.prompt('Motif du refus (transmis au collaborateur) :');
    if (motif === null) return;
    if (!motif.trim()) { setErreur('Le motif de refus est obligatoire'); return; }
    setEnvoi(true);
    try {
      await validation.refuser(selection.note.id, motif);
      setSelection(null);
      charger();
    } catch (err) {
      setErreur(err.response?.data?.error || 'Refus impossible');
    } finally {
      setEnvoi(false);
    }
  };

  const detail = () => {
    const { note, depenses } = selection;
    const total = depenses.reduce((s, d) => s + (parseFloat(d.montant_ttc) || 0), 0);
    const totalEcarte = depenses
      .filter((d) => d.id in ecartees)
      .reduce((s, d) => s + (parseFloat(d.montant_ttc) || 0), 0);
    return (
      <>
        <button type="button" className="fd-retour vl-retour-mobile" onClick={() => setSelection(null)}>← Notes en attente</button>
        <h2 className="nf-titre-page">
          {note.prenom} {note.nom || note.user_email}
          {note.dossier_libelle ? ` — 📁 ${note.dossier_libelle}` : ` — ${note.periode}`}
          {note.societe_nom ? ` · ${note.societe_nom}` : ''}
        </h2>
        {erreur && <div className="nf-erreur-bandeau">{erreur}</div>}

        <ul className="vl-lignes">
          {depenses.map((d) => {
            const ecartee = d.id in ecartees;
            return (
              <li key={d.id} className={`vl-ligne ${ecartee ? 'ecartee' : ''}`}>
                <div className="vl-ligne-haut">
                  <span className="vl-icone">{d.categorie_icone || '🧾'}</span>
                  <span className="vl-corps">
                    <span className="vl-titre">
                      {d.type === 'km' ? `${km(d.distance_km)} — ${d.motif_deplacement || 'déplacement'}` : (d.marchand || 'Dépense')}
                    </span>
                    <span className="vl-sous">
                      {d.date_depense ? dateFr(d.date_depense) : 'date ?'}
                      {d.categorie_libelle ? ` · ${d.categorie_libelle}` : ''}
                      {d.moyen_paiement === 'carte_societe' ? ' · carte société' : ''}
                    </span>
                    {d.alertes.length > 0 && (
                      <span className="vl-alertes">{d.alertes.map((a) => <span key={a} className="nf-alerte">{a}</span>)}</span>
                    )}
                  </span>
                  <span className="vl-droite">
                    <span className="nf-montant">{d.montant_ttc !== null ? eur(d.montant_ttc) : '—'}</span>
                    {d.justificatif_url && (
                      <button type="button" className="nt-justif"
                        onClick={() => setApercu({ url: d.justificatif_url, type: d.justificatif_type, titre: d.marchand || 'Justificatif' })}>
                        justificatif
                      </button>
                    )}
                  </span>
                </div>
                <div className="vl-ligne-bas">
                  <label className="vl-ecarter">
                    <input type="checkbox" checked={ecartee} onChange={() => basculerEcart(d.id)} />
                    Écarter cette ligne
                  </label>
                  {ecartee && (
                    <input
                      type="text"
                      className="vl-motif"
                      placeholder="Motif (obligatoire) — ex. justificatif illisible"
                      value={ecartees[d.id]}
                      onChange={(e) => setEcartees((x) => ({ ...x, [d.id]: e.target.value }))}
                    />
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {apercu && <Visionneuse {...apercu} onClose={() => setApercu(null)} />}
        <div className="vl-actions">
          <button type="button" className="nf-btn nf-btn--danger" onClick={refuser} disabled={envoi}>
            Refuser toute la note
          </button>
          <button type="button" className="nf-btn nf-btn--principal vl-valider" onClick={valider} disabled={envoi}>
            {Object.keys(ecartees).length
              ? `Valider ${eur(total - totalEcarte)} (${Object.keys(ecartees).length} ligne(s) écartée(s))`
              : `Tout valider (${eur(total)})`}
          </button>
        </div>
      </>
    );
  };

  return (
    <div className="vl-layout">
      <div className={`vl-pane-liste ${selection ? 'vl-cache-mobile' : ''}`}>
        <h2 className="nf-titre-page">Notes en attente de validation</h2>
        {!selection && erreur && <div className="nf-erreur-bandeau">{erreur}</div>}
        {liste.length === 0 ? (
          <div className="nf-vide">
            <h2>Rien à valider</h2>
            <p>Les notes soumises par votre équipe apparaîtront ici.</p>
          </div>
        ) : (
          <ul className="nt-liste">
            {liste.map((n) => (
              <li key={n.id}>
                <button type="button"
                  className={`vl-carte ${selection?.note?.id === n.id ? 'vl-carte--active' : ''}`}
                  onClick={() => ouvrir(n.id)}>
                  <span className="vl-carte-rangee">
                    <span className="vl-carte-gauche">
                      <span className="vl-carte-nom">{n.prenom} {n.nom || n.user_email}</span>
                      {n.dossier_libelle && <span className="vl-carte-dossier">📁 {n.dossier_libelle}</span>}
                      <span className="vl-carte-sous">
                        {n.nb_lignes} dépense{n.nb_lignes > 1 ? 's' : ''}
                        {n.soumise_le ? ` · soumise le ${new Date(n.soumise_le).toLocaleDateString('fr-FR')}` : ''}
                        {n.societe_nom ? ` · ${n.societe_nom}` : ''}
                      </span>
                    </span>
                    <span className="nf-montant vl-carte-total">{eur(n.total_ttc)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={`vl-pane-detail ${selection ? '' : 'vl-cache-mobile'}`}>
        {selection ? detail() : (
          <div className="vl-placeholder">
            <p>Sélectionnez une note à gauche pour la contrôler.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Validation;
